import React, { createContext, useContext, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { writeBatch } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import {
  practiceSummary, totalMinutes, practiceSessionDocument, sortPracticeSessions,
} from '../domain/practice';
import { freeId } from '../domain/freeId';
import { useCollectionWindow } from './useCollectionWindow';

// Practice sessions. Genuine appends — two sessions of the same length on the
// same day are two sessions — so these use random ids, unlike the records
// derived from an attendance decision.

const PracticeContext = createContext(null);
const TRACK_LIMIT = 5000;

export function PracticeProvider({ children }) {
  const { user } = useAuth();

  // The practice-entries window, from whichever read path serves this session
  // (#207 U5). `withMetadata` keeps the snapshot path's `includeMetadataChanges`
  // as the rollback; `pendingWrite` is spread by the default row.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.practiceEntries,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const entries = read.docs;

  const byStudent = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      if (!m.has(e.studentId)) m.set(e.studentId, []);
      m.get(e.studentId).push(e);
    }
    for (const [id, list] of m) m.set(id, sortPracticeSessions(list));
    return m;
  }, [entries]);

  const value = useMemo(() => ({
    entries,
    // Exposed because every window hook has it, not for any one caller;
    // nothing turns on this flag here (a delete asks the server, #318).
    loading: read.loading,
    // The read stopped before it answered (#647) — src/domain/reading.js.
    failed: read.failed,
    forStudent: (id) => byStudent.get(id) || [],
    summaryFor: (id, asOf) => practiceSummary(byStudent.get(id) || [], asOf),
    minutesFor: (id) => totalMinutes(byStudent.get(id) || []),

    addSession: async ({ studentId, practicedOn, minutes, note }) => {
      if (!user) return null;
      const id = freeId('prc');
      const batch = writeBatch(db);
      // Both halves of the tombstone, written live. See domain/practice.js.
      batch.set(
        docRef(user.uid, COLLECTIONS.practiceEntries, id),
        practiceSessionDocument({ studentId, practicedOn, minutes, note }, serverTimestamp()),
      );
      await batch.commit();
      return id;
    },

    removeSession: async (id) => {
      if (!user) return;
      const batch = writeBatch(db);
      batch.delete(docRef(user.uid, COLLECTIONS.practiceEntries, id));
      await batch.commit();
    },
  }), [entries, read.loading, read.failed, byStudent, user]);

  return <PracticeContext.Provider value={value}>{children}</PracticeContext.Provider>;
}

export function usePractice() {
  const ctx = useContext(PracticeContext);
  if (!ctx) throw new Error('usePractice must be used within a PracticeProvider');
  return ctx;
}
